import { useEffect, useRef } from "react";

const SCRIPT_ID = "cf-turnstile-script";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile widget.
 *
 * Props:
 *   onVerify(token)  — called when the user passes the challenge
 *   onExpire()       — called when the token expires (user must retry)
 *   onError()        — called on widget error
 *   theme            — "light" | "dark" | "auto"  (default: "auto")
 *
 * The widget is invisible / managed — Cloudflare decides when to show
 * a visual challenge. Most real users never see anything.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not set the widget renders nothing
 * and onVerify is called immediately with a placeholder token so the
 * forms still work during local development without Cloudflare keys.
 */
export default function TurnstileWidget({
  onVerify,
  onExpire,
  onError,
  theme = "auto",
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // ── Dev bypass: if no site key, call onVerify immediately ────────────────
  useEffect(() => {
    if (!SITE_KEY) {
      onVerify?.("__dev_bypass__");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Production: load Cloudflare script + render widget ───────────────────
  useEffect(() => {
    if (!SITE_KEY) return; // nothing to do in dev

    const render = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme,
        callback: (token) => onVerify?.(token),
        "expired-callback": () => {
          widgetIdRef.current = null;
          onExpire?.();
        },
        "error-callback": () => {
          widgetIdRef.current = null;
          onError?.();
        },
      });
    };

    if (window.turnstile) {
      // Script already loaded (e.g. hot-reload)
      render();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    } else {
      // Script tag exists but not loaded yet — poll
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 100);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  if (!SITE_KEY) return null; // invisible in dev

  return <div ref={containerRef} className="mt-3" />;
}
